"use client";

import { ArrowLeft, MapPin, Calendar, Clock, Users, Wine } from 'lucide-react';
import Link from 'next/link';
import { ImageWithFallback } from '@/figma/components/figma/ImageWithFallback';
import type { TourPlanningIcon, ToursPageContent } from '@/lib/page-content-types';

const tours = [
  {
    slug: 'judean-hills-premium',
    title: 'Judean Hills Wine Trail',
    duration: 'Full Day',
    region: 'Judean Hills',
    wineries: 3,
    description: 'Visit three boutique wineries in the historic hills around Jerusalem. Taste award-winning wines and explore ancient terroir.',
    highlights: ['Domaine du Castel', 'Tzora Vineyards', 'Clos de Gat'],
    price: '$180',
    image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800'
  },
  {
    slug: 'galilee-heights-experience',
    title: 'Galilee Heights Experience',
    duration: 'Full Day',
    region: 'Galilee',
    wineries: 4,
    description: 'Explore the cool-climate vineyards of Upper Galilee. Mountain views and elegant wines.',
    highlights: ['Galil Mountain', 'Dalton Winery', 'Recanati', 'Lunch included'],
    price: '$165',
    image: 'https://images.unsplash.com/photo-1474557157379-8aa74a6ef541?w=800'
  },
  {
    slug: 'golan-heights-private-tour',
    title: 'Golan Heights Private Tour',
    duration: '6 hours',
    region: 'Golan Heights',
    wineries: 2,
    description: 'Premium private tour of Israel\'s most prestigious wine region. Volcanic soils and world-class wines.',
    highlights: ['Yarden Winery', 'Private tastings', 'Winemaker meeting'],
    price: '$320',
    image: 'https://images.unsplash.com/photo-1523825391079-0092e2f2baaf?w=800'
  }
];

const events = [
  {
    title: 'Israeli Wine Festival 2026',
    date: 'May 15-17, 2026',
    location: 'Tel Aviv Port',
    type: 'Festival',
    description: '100+ wineries, masterclasses, food pairings, and live music celebrating Israeli wine culture.'
  },
  {
    title: 'Harvest Season Tours',
    date: 'August - September 2026',
    location: 'Multiple Regions',
    type: 'Seasonal',
    description: 'Experience grape harvest firsthand. Limited availability during peak harvest weeks.'
  },
  {
    title: 'Kosher Wine Masterclass',
    date: 'March 8, 2026',
    location: 'Jerusalem',
    type: 'Educational',
    description: 'Deep dive into kosher winemaking with industry experts and vertical tastings.'
  },
  {
    title: 'Collectors\' Dinner Series',
    date: 'Quarterly',
    location: 'Various',
    type: 'Exclusive',
    description: 'Intimate dinners featuring rare library vintages and renowned Israeli chefs.'
  }
];

const regions = [
  {
    name: 'Judean Hills',
    wineries: 45,
    description: 'Historic region with Jerusalem-area wineries',
    distance: '30 min from Jerusalem',
    bestTime: 'Spring & Fall'
  },
  {
    name: 'Galilee',
    wineries: 62,
    description: 'Cool-climate, high-elevation vineyards',
    distance: '2 hours from Tel Aviv',
    bestTime: 'Year-round'
  },
  {
    name: 'Golan Heights',
    wineries: 18,
    description: 'Premium volcanic terroir',
    distance: '2.5 hours from Tel Aviv',
    bestTime: 'Spring & Summer'
  },
  {
    name: 'Negev',
    wineries: 12,
    description: 'Desert wine frontier',
    distance: '1 hour from Be\'er Sheva',
    bestTime: 'Winter & Spring'
  }
];

const planningTips = [
  {
    icon: 'calendar' as TourPlanningIcon,
    title: 'Best Times to Visit',
    content: 'Spring (March-May) and fall (September-November) offer ideal weather. Harvest season (August-September) is magical but busy.'
  },
  {
    icon: 'clock' as TourPlanningIcon,
    title: 'Timing Your Day',
    content: 'Most wineries open 9am-5pm. Book tastings in advance. Plan 1.5-2 hours per winery visit.'
  },
  {
    icon: 'users' as TourPlanningIcon,
    title: 'Group Size',
    content: 'Private tours for 2-8 people offer the best experience. Larger groups should book dedicated tour buses.'
  },
  {
    icon: 'wine' as TourPlanningIcon,
    title: 'Tasting Etiquette',
    content: 'Dress casually but neat. Designate a driver. Tip guides 10-15%. Ask questions - winemakers love to share!'
  }
];

const planningIconMap = {
  calendar: Calendar,
  clock: Clock,
  users: Users,
  wine: Wine,
} as const;

export function ToursPage({ content }: { content?: ToursPageContent | null }) {
  const toursData = content?.tours?.length ? content.tours : tours;
  const eventsData = content?.events?.length ? content.events : events;
  const regionsData = content?.regions?.length ? content.regions : regions;
  const planningTipsData = content?.planningTips?.length ? content.planningTips : planningTips;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <div className="mb-8">
                <span 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 300,
                    color: 'var(--slate)',
                    letterSpacing: '0.05em'
                  }}
                >
                  03
                </span>
              </div>
              
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Tours & Events
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                Plan your perfect wine country adventure. Curated tours, seasonal events, and insider tips for exploring Israel&apos;s wine regions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tours */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2 
                className="mb-4"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Curated Wine Tours
              </h2>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Professionally guided experiences designed for wine enthusiasts
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-3">
              {toursData.map((tour) => (
                <Link
                  key={tour.title}
                  href={`/tours/${tour.slug}`}
                  style={{ backgroundColor: 'var(--paper)' }}
                  className="group cursor-pointer transition-all duration-300 hover:shadow-lg"
                >
                  <div className="aspect-[4/3] overflow-hidden" style={{ backgroundColor: 'var(--stone)' }}>
                    <ImageWithFallback
                      src={tour.image}
                      alt={tour.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <span 
                        className="px-3 py-1"
                        style={{ 
                          backgroundColor: 'var(--oxblood)',
                          color: 'var(--paper)',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 400,
                          letterSpacing: '0.05em'
                        }}
                      >
                        {tour.duration.toUpperCase()}
                      </span>
                      <p 
                        className="flex items-center gap-1"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--slate)',
                          fontWeight: 400
                        }}
                      >
                        <MapPin size={12} />
                        {tour.region}
                      </p>
                    </div>
                    
                    <h3 
                      className="mb-3"
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-xl)',
                        fontWeight: 300,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {tour.title}
                    </h3>
                    
                    <p 
                      className="mb-4"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        lineHeight: 'var(--line-height-body)',
                        color: 'var(--slate)',
                        fontWeight: 300
                      }}
                    >
                      {tour.description}
                    </p>

                    <div className="mb-6">
                      <p 
                        className="mb-2"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--charcoal)',
                          fontWeight: 500,
                          letterSpacing: '0.05em'
                        }}
                      >
                        HIGHLIGHTS
                      </p>
                      <ul className="space-y-1">
                        {tour.highlights.map((highlight) => (
                          <li 
                            key={highlight}
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--slate)',
                              fontWeight: 300
                            }}
                          >
                            • {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid var(--stone)' }}>
                      <span 
                        style={{ 
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-xl)',
                          fontWeight: 300,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {tour.price}
                      </span>
                      <span 
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          color: 'var(--slate)',
                          fontWeight: 400
                        }}
                      >
                        per person
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2 
                className="mb-4"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Upcoming Events
              </h2>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Festivals, tastings, and exclusive wine experiences
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {eventsData.map((event) => (
                <div 
                  key={event.title}
                  className="p-10 border cursor-pointer transition-all duration-200 hover:bg-[var(--linen)]"
                  style={{ borderColor: 'var(--stone)' }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span 
                      className="px-3 py-1"
                      style={{ 
                        backgroundColor: 'var(--olive-mist)',
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--charcoal)',
                        fontWeight: 400,
                        letterSpacing: '0.05em'
                      }}
                    >
                      {event.type.toUpperCase()}
                    </span>
                  </div>
                  
                  <h3 
                    className="mb-3"
                    style={{ 
                      fontFamily: 'var(--font-display)',
                      fontSize: 'var(--text-xl)',
                      fontWeight: 300,
                      color: 'var(--charcoal)'
                    }}
                  >
                    {event.title}
                  </h3>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <p 
                      className="flex items-center gap-2"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--oxblood)',
                        fontWeight: 400
                      }}
                    >
                      <Calendar size={14} />
                      {event.date}
                    </p>
                    <p 
                      className="flex items-center gap-2"
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 400
                      }}
                    >
                      <MapPin size={14} />
                      {event.location}
                    </p>
                  </div>
                  
                  <p 
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-sm)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    {event.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Wine Regions Map */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2 
                className="mb-4"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Explore by Region
              </h2>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Plan your route through Israel&apos;s wine country
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {regionsData.map((region) => (
                <div 
                  key={region.name}
                  className="p-8 cursor-pointer transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: 'var(--paper)' }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 
                      style={{ 
                        fontFamily: 'var(--font-display)',
                        fontSize: 'var(--text-2xl)',
                        fontWeight: 300,
                        color: 'var(--charcoal)'
                      }}
                    >
                      {region.name}
                    </h3>
                    <span 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 400
                      }}
                    >
                      {region.wineries} wineries
                    </span>
                  </div>
                  
                  <p 
                    className="mb-4"
                    style={{ 
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-base)',
                      lineHeight: 'var(--line-height-body)',
                      color: 'var(--slate)',
                      fontWeight: 300
                    }}
                  >
                    {region.description}
                  </p>

                  <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid var(--stone)' }}>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 400
                      }}
                    >
                      {region.distance}
                    </p>
                    <span style={{ color: 'var(--stone)' }}>•</span>
                    <p 
                      style={{ 
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-sm)',
                        color: 'var(--slate)',
                        fontWeight: 400
                      }}
                    >
                      Best: {region.bestTime}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Planning Tips */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2 
                className="mb-4"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Planning Your Visit
              </h2>
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Essential tips for wine touring in Israel
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              {planningTipsData.map((tip) => {
                const TipIcon = planningIconMap[tip.icon];
                return (
                  <div key={tip.title} className="flex gap-6">
                    <div
                      className="flex-shrink-0 w-12 h-12 flex items-center justify-center"
                      style={{ backgroundColor: 'var(--linen)' }}
                    >
                      <TipIcon size={24} style={{ color: 'var(--oxblood)' }} />
                    </div>
                    <div className="flex-1">
                      <h3
                        className="mb-3"
                        style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-xl)',
                          fontWeight: 300,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {tip.title}
                      </h3>
                      <p
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-base)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {tip.content}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
